import React from "react";
import { Box, Typography, Link, Grid } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { acme } from "../../theme/acmeTheme";
import BrandLogoMark from "../ui/BrandLogoMark";
import CookiePreferencesManager from "../CookiePreferencesManager";

const LAUNCH_YEAR = 2025;
const ATIKUT_LOGO = `${process.env.PUBLIC_URL || ""}/images/logo_company/logo_black_atikut.svg`;

function getCopyrightYearRange() {
  const currentYear = new Date().getFullYear();
  return currentYear > LAUNCH_YEAR
    ? `${LAUNCH_YEAR}–${currentYear}`
    : `${LAUNCH_YEAR}`;
}

const footerLinkSx = {
  color: acme.colors.mutedForegroundSolid,
  fontSize: "0.875rem",
  textDecoration: "none",
  transition: "color 0.15s",
  "&:hover": { color: acme.colors.foreground },
};

const footerSections = [
  {
    title: "Product",
    links: [
      { text: "Email Campaigns", href: "/register" },
      { text: "SMS Campaigns", href: "/register" },
    ],
  },
  {
    title: "Company",
    links: [{ text: "About Us", href: "/about" }],
  },
];

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        mt: "auto",
        borderTop: `1px solid ${acme.colors.border}`,
        backgroundColor: acme.colors.background,
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: { xs: 5, md: 7 },
        }}
      >
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={5}>
            <BrandLogoMark sx={{ mb: 2.5 }} />
            <Typography
              sx={{
                color: acme.colors.mutedForegroundSolid,
                lineHeight: 1.7,
                mb: 3,
                maxWidth: 320,
                fontSize: "0.9375rem",
              }}
            >
              Transform your business with powerful email and SMS marketing that
              actually converts.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <EmailIcon
                  sx={{ color: acme.colors.iconDefault, fontSize: 18 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: acme.colors.mutedForegroundSolid }}
                >
                  support@mailsball.com
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <LocationOnIcon
                  sx={{ color: acme.colors.iconDefault, fontSize: 18 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: acme.colors.mutedForegroundSolid }}
                >
                  Tallinn, Estonia
                </Typography>
              </Box>
            </Box>
          </Grid>

          {footerSections.map((section) => (
            <Grid item xs={6} sm={4} md={2} key={section.title}>
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: acme.colors.foreground,
                  fontSize: "0.9375rem",
                }}
              >
                {section.title}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {section.links.map((link) => (
                  <Link key={link.text} href={link.href} sx={footerLinkSx}>
                    {link.text}
                  </Link>
                ))}
              </Box>
            </Grid>
          ))}

          <Grid item xs={12} sm={8} md={3}>
            <Typography
              sx={{
                fontWeight: 600,
                mb: 2,
                color: acme.colors.foreground,
                fontSize: "0.9375rem",
              }}
            >
              Follow us
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {[
                {
                  icon: FacebookIcon,
                  href: "https://www.facebook.com/mailsball/",
                },
                {
                  icon: LinkedInIcon,
                  href: "https://www.linkedin.com/company/mailsball/",
                },
              ].map(({ icon: Icon, href }) => (
                <Box
                  key={href}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 36,
                    height: 36,
                    borderRadius: acme.radius.md,
                    border: `1px solid ${acme.colors.border}`,
                    color: acme.colors.mutedForegroundSolid,
                    transition: "all 0.15s",
                    "&:hover": {
                      color: acme.colors.foreground,
                      borderColor: acme.colors.foreground,
                      backgroundColor: acme.colors.muted,
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ borderTop: `1px solid ${acme.colors.border}` }}>
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 2, md: 4 },
            py: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{ color: acme.colors.mutedForegroundSolid }}
            >
              © {getCopyrightYearRange()} Atikut Company. All rights reserved.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: acme.colors.mutedForegroundSolid,
                mt: 0.75,
                display: "block",
                maxWidth: 420,
                lineHeight: 1.5,
              }}
            >
              This site is protected by reCAPTCHA and the Google{" "}
              <Link
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "inherit" }}
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "inherit" }}
              >
                Terms of Service
              </Link>{" "}
              apply.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Link component={RouterLink} to="/privacy-policy" sx={footerLinkSx}>
              Privacy Policy
            </Link>
            <Link
              component={RouterLink}
              to="/terms-of-service"
              sx={footerLinkSx}
            >
              Terms of Service
            </Link>
            <CookiePreferencesManager
              buttonColor="inherit"
              sx={{
                ...footerLinkSx,
                p: 0,
                minWidth: 0,
                "&:hover": {
                  color: acme.colors.foreground,
                  backgroundColor: "transparent",
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${acme.colors.border}`,
          backgroundColor: acme.colors.pageBg,
          py: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "0.6875rem",
              color: acme.colors.mutedForegroundSolid,
              letterSpacing: "0.02em",
              textTransform: "lowercase",
            }}
          >
            from
          </Typography>
          <Link
            href="https://atikut.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Atikut"
            sx={{
              display: "inline-flex",
              lineHeight: 0,
              opacity: 0.85,
              transition: "opacity 0.15s",
              "&:hover": { opacity: 1 },
            }}
          >
            <Box
              component="img"
              src={ATIKUT_LOGO}
              alt="Atikut"
              sx={{
                height: 22,
                width: "auto",
                display: "block",
              }}
            />
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

export default Footer;
